import { useState } from 'react'
import { useRegistrarVersaoDeCriterio } from '../../../api/queries/parametrosContratuais'
import { Botao } from '../../../components/Botao'
import { Dialogo } from '../../../components/Dialogo'
import { AvisoDeErro } from '../../../components/Estados'
import { CampoDeSelecao, CampoDeTexto, CampoDeTextoLongo } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'
import type { SeloDoCriterio, VersaoDeCriterio } from '../../../types/parametroContratual'

interface Props {
  /** A linha do tempo inteira: é dela que sai a lista de chaves e o valor que se copia. */
  versoes: VersaoDeCriterio[]
  aoFechar: () => void
}

/**
 * Registrar uma versão nova de um critério.
 *
 * **Não é um formulário de edição, e o diálogo diz isso.** A versão anterior não é substituída: ela
 * tem a vigência encerrada e continua na lista, porque é ela que explica o que já foi apurado. O
 * banco recusa `UPDATE` e `DELETE` nesta tabela pela mesma razão de `gmud_transicao` e do
 * fechamento — reescrever por cima apaga a prova de qual critério sustentou o desconto.
 *
 * **O ato é obrigatório quando o selo é "formalizado", e a checagem existe nos dois lados.** Aqui,
 * para a pessoa não perder o que digitou; no banco, porque é o `CHECK` que impede o selo de
 * afirmar um ato que não existe.
 *
 * @param props a linha do tempo e o que fazer ao fechar
 * @returns o diálogo
 */
export function DialogoDeVersaoDeCriterio({ versoes, aoFechar }: Props) {
  const t = TEXTOS.referencias.criterios.novaVersao
  const chaves = [...new Set(versoes.map((v) => v.chave))]
  const [chave, setChave] = useState(chaves[0] ?? '')
  // A lista vem da vigência mais recente para a mais antiga: a primeira ocorrência da chave é a
  // última versão dela, que é justamente a que se copia para alterar só o que muda.
  const atual = versoes.find((v) => v.chave === chave)
  const [valor, setValor] = useState(atual?.valor ?? '')
  const [vigencia, setVigencia] = useState('')
  const [selo, setSelo] = useState<SeloDoCriterio>('PROVISORIO')
  const [fonte, setFonte] = useState(atual?.fonteTr ?? '')
  const [fundamento, setFundamento] = useState('')
  const [risco, setRisco] = useState('')
  const [ato, setAto] = useState('')
  const registrar = useRegistrarVersaoDeCriterio()

  function trocarCriterio(nova: string) {
    setChave(nova)
    const versaoAtual = versoes.find((v) => v.chave === nova)
    setValor(versaoAtual?.valor ?? '')
    setFonte(versaoAtual?.fonteTr ?? '')
  }

  const faltaAto = selo === 'FORMALIZADO' && ato.trim().length === 0
  const incompleto =
    !chave ||
    !valor.trim() ||
    !/^\d{4}-\d{2}$/.test(vigencia) ||
    !fonte.trim() ||
    !fundamento.trim() ||
    faltaAto

  const confirmar = () =>
    registrar.mutate(
      {
        chave,
        valor: valor.trim(),
        vigenciaInicio: vigencia,
        status: selo,
        fonteTr: fonte.trim(),
        fundamento: fundamento.trim(),
        riscoResidual: risco.trim() || undefined,
        atoFormal: ato.trim() || undefined,
      },
      { onSuccess: aoFechar },
    )

  return (
    <Dialogo
      titulo={t.titulo}
      descricao={t.ajuda}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao aparencia="secundario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao aparencia="primario" onClick={confirmar} disabled={incompleto || registrar.isPending}>
            {t.registrar}
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <CampoDeSelecao rotulo={t.criterio} value={chave} onChange={(e) => trocarCriterio(e.target.value)}>
          {chaves.map((uma) => (
            <option key={uma} value={uma}>
              {uma}
            </option>
          ))}
        </CampoDeSelecao>

        <CampoDeTextoLongo
          rotulo={t.valor}
          ajuda={t.valorAjuda}
          rows={5}
          value={valor}
          onChange={(e) => setValor(e.target.value)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <CampoDeTexto
            rotulo={t.vigencia}
            ajuda={t.vigenciaAjuda}
            placeholder="2026-09"
            value={vigencia}
            onChange={(e) => setVigencia(e.target.value)}
          />
          <CampoDeSelecao
            rotulo={t.selo}
            value={selo}
            onChange={(e) => setSelo(e.target.value as SeloDoCriterio)}
          >
            <option value="PROVISORIO">{TEXTOS.referencias.criterios.selo.PROVISORIO}</option>
            <option value="FORMALIZADO">{TEXTOS.referencias.criterios.selo.FORMALIZADO}</option>
            <option value="INDETERMINADO">{TEXTOS.referencias.criterios.selo.INDETERMINADO}</option>
          </CampoDeSelecao>
        </div>

        <p className="text-xs text-slate-500">{TEXTOS.referencias.criterios.seloAjuda[selo]}</p>

        <CampoDeTexto
          rotulo={t.fonte}
          ajuda={t.fonteAjuda}
          value={fonte}
          onChange={(e) => setFonte(e.target.value)}
        />
        <CampoDeTextoLongo
          rotulo={t.fundamento}
          rows={3}
          value={fundamento}
          onChange={(e) => setFundamento(e.target.value)}
        />
        <CampoDeTextoLongo rotulo={t.risco} rows={2} value={risco} onChange={(e) => setRisco(e.target.value)} />

        {selo === 'FORMALIZADO' && (
          <CampoDeTexto rotulo={t.ato} ajuda={t.atoAjuda} value={ato} onChange={(e) => setAto(e.target.value)} />
        )}

        {registrar.isError && <AvisoDeErro erro={registrar.error} titulo={t.naoRegistrou} />}
      </div>
    </Dialogo>
  )
}
